import { useState, useRef, useEffect, useCallback } from "react";
import useWindowSize from "../hooks/useWindowSize";
import apiService from "../services/api";
import { validateDateOfBirth, validateAdmissionDate, calculateAge } from "../utils/dateValidation";

/* ═══════════════════════ CONSTANTS ═══════════════════════ */
const W = "#ffffff";
const CARD_BG = "#042558";
const INPUT_BG = "rgba(255,255,255,0.08)";
const BORDER = "rgba(255,255,255,0.3)";
const ACCENT = "#4A90E2";
const GREEN = "#6ADD00";

const MOBILITY_OPTIONS = ["INDEPENDENT", "ASSISTED", "WHEELCHAIR"];
const GENDER_OPTIONS = ["M", "F"];
const RISK_LEVEL_OPTIONS = ["LOW", "MODERATE", "HIGH", "CRITICAL"];
const RISK_FACTOR_OPTIONS = [
  "diabetes",
  "neuropathy",
  "polypharmacy",
  "nocturia",
  "cognitive_impairment",
  "vision_impairment",
];

const EMPTY_CONTACT = {
  contact_name: "",
  relationship: "",
  phone: "",
  email: "",
  contact_priority: "",
  notify_on_fall: false,
  is_legal_guardian: false,
};
const PRIORITY_OPTIONS = ["PRIMARY", "SECONDARY", "TERTIARY"];
const RELATIONSHIP_OPTIONS = ["Spouse", "Son", "Daughter", "Sibling", "Friend", "Other"];

/* ═══════════════════════ HELPER COMPONENTS ═══════════════════════ */
const DetailItem = ({ label, value, valueColor = "#ffffff" }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: "rgba(255,255,255,0.6)",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: valueColor,
        }}
      >
        {value}
      </span>
    </div>
  );
};

/* ═══════════════════════ STYLED INPUT COMPONENT ═══════════════════════ */
function FormField({ label, children, required, isMobile }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label
        style={{
          fontSize: isMobile ? 12 : 14,
          fontWeight: 600,
          color: W,
          opacity: 0.85,
        }}
      >
        {label} {required && <span style={{ color: "#FF6B6B" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, type = "text", ...rest }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        background: INPUT_BG,
        border: `1.5px solid ${BORDER}`,
        borderRadius: 10,
        padding: "12px 16px",
        fontSize: 14,
        color: W,
        fontFamily: "'Segoe UI', sans-serif",
        outline: "none",
        transition: "border-color 0.2s",
        width: "100%",
        boxSizing: "border-box",
      }}
      onFocus={(e) => (e.target.style.borderColor = ACCENT)}
      onBlur={(e) => (e.target.style.borderColor = BORDER)}
      {...rest}
    />
  );
}

function SelectInput({ value, onChange, options, placeholder }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: INPUT_BG,
        border: `1.5px solid ${BORDER}`,
        borderRadius: 10,
        padding: "12px 16px",
        fontSize: 14,
        color: value ? W : "rgba(255,255,255,0.4)",
        fontFamily: "'Segoe UI', sans-serif",
        outline: "none",
        cursor: "pointer",
        width: "100%",
        boxSizing: "border-box",
        appearance: "none",
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l5 5 5-5' stroke='white' stroke-width='2' fill='none'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 16px center",
      }}
    >
      <option value="" style={{ background: "#0e2240", color: W }}>
        {placeholder || "Select..."}
      </option>
      {options.map((opt) => (
        <option key={opt} value={opt} style={{ background: "#0e2240", color: W }}>
          {opt}
        </option>
      ))}
    </select>
  );
}

/* ═══════════════════════ PHOTO UPLOAD COMPONENT ═══════════════════════ */
function PhotoUpload({ photoPreview, onPhotoSelect, isMobile }) {
  const fileInputRef = useRef(null);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) onPhotoSelect(file);
  }, [onPhotoSelect]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 16,
      }}
    >
      {/* Photo Preview Circle */}
      <div
        onClick={handleClick}
        style={{
          width: isMobile ? 120 : 150,
          height: isMobile ? 120 : 150,
          borderRadius: "50%",
          border: `3px dashed ${photoPreview ? GREEN : BORDER}`,
          background: photoPreview
            ? "transparent"
            : "rgba(255,255,255,0.05)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          overflow: "hidden",
          transition: "all 0.3s",
          position: "relative",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = ACCENT;
          e.currentTarget.style.transform = "scale(1.03)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = photoPreview ? GREEN : BORDER;
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        {photoPreview ? (
          <img
            src={photoPreview}
            alt="Preview"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "50%",
            }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
            }}
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke={W}
              strokeWidth="1.5"
              strokeLinecap="round"
              style={{ opacity: 0.5 }}
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
            <span
              style={{
                fontSize: 11,
                color: W,
                opacity: 0.5,
                textAlign: "center",
              }}
            >
              Click to upload
            </span>
          </div>
        )}

        {/* Hover overlay when photo exists */}
        {photoPreview && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0,
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = 0)}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke={W}
              strokeWidth="2"
            >
              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      <span
        style={{
          fontSize: 12,
          color: W,
          opacity: 0.5,
          textAlign: "center",
        }}
      >
        JPG, PNG or WebP (max 5MB)
      </span>
    </div>
  );
}

/* ═══════════════════════ CHECKBOX GROUP ═══════════════════════ */
function CheckboxGroup({ options, selected, onChange, isMobile }) {
  const toggle = (opt) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((s) => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
      }}
    >
      {options.map((opt) => {
        const isActive = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            style={{
              background: isActive
                ? "rgba(74,144,226,0.3)"
                : "rgba(255,255,255,0.05)",
              border: `1.5px solid ${
                isActive ? "rgba(74,144,226,0.7)" : BORDER
              }`,
              borderRadius: 8,
              padding: isMobile ? "6px 10px" : "8px 14px",
              fontSize: isMobile ? 11 : 13,
              fontWeight: isActive ? 700 : 400,
              color: W,
              cursor: "pointer",
              transition: "all 0.15s",
              whiteSpace: "nowrap",
            }}
          >
            {isActive && (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke={GREEN}
                strokeWidth="3"
                style={{ marginRight: 6, verticalAlign: "middle" }}
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
            {opt.replace(/_/g, " ")}
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════ MAIN PAGE ═══════════════════════ */
export default function AddSeniorPage({ onBack, onSeniorAdded, user }) {
  const { isMobile } = useWindowSize();

  // Get facility name from user session
  const facilityName = user?.facilityName || "Facility";

  // Form state — everything starts empty, user fills what they have
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [age, setAge] = useState("");

    // Auto-calculate age when DOB changes
    useEffect(() => {
      if (dob && dob.trim()) {
        const calculatedAge = calculateAge(dob);
        if (calculatedAge !== null) {
          setAge(calculatedAge.toString());
        } else {
          setAge('');
        }
      } else {
        setAge('');
      }
    }, [dob]);
    const [gender, setGender] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [weightKg, setWeightKg] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [unitId, setUnitId] = useState("");
    const [mobilityClass, setMobilityClass] = useState("");
  const [admissionDate, setAdmissionDate] = useState(() => {
    // Set admission date to today's date by default
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [mrn, setMrn] = useState("");
  const [fallRiskLevel, setFallRiskLevel] = useState("");
  const [riskFactors, setRiskFactors] = useState([]);
  const [sleepMonitoringConsent, setSleepMonitoringConsent] = useState(false);
  const [videoClipConsent, setVideoClipConsent] = useState(false);
  const [sleepQuality, setSleepQuality] = useState("");

  // Emergency contacts — start with one blank row
  const [emergencyContacts, setEmergencyContacts] = useState([{ ...EMPTY_CONTACT }]);

    // Photo state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

        // Submission state
        const [submitting, setSubmitting] = useState(false);
        const [error, setError] = useState(null);
        const [createdResident, setCreatedResident] = useState(null);
        const [showPreview, setShowPreview] = useState(false);
        const [showSuccessCard, setShowSuccessCard] = useState(false);
  
  // Existing residents for duplicate checking
  const [existingResidents, setExistingResidents] = useState([]);
  
  // Load existing residents on mount for duplicate checking
  useEffect(() => {
    const loadResidents = async () => {
      try {
        const data = await apiService.getAllResidents();
        setExistingResidents(data.residents || []);
      } catch (err) {
        console.error('Failed to load residents for validation:', err);
        // Don't block the form if we can't load residents
      }
    };
    loadResidents();
  }, []);

  const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
  const ALLOWED_PHOTO_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp"];

  const handlePhotoSelect = (file) => {
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_PHOTO_TYPES.includes(file.type) || !ALLOWED_PHOTO_EXTENSIONS.includes(ext)) {
      setError("Invalid file type. Only JPG, PNG, and WebP images are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Photo must be less than 5MB");
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
    setError(null);
  };

          // Phone number validation helper
  const validatePhoneNumber = (phone) => {
    if (!phone || phone.trim() === "") return true; // Optional field
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    // Must be 10 digits (US format)
    return digitsOnly.length === 10;
  };

  // Email validation helper
  const validateEmail = (email) => {
    if (!email || email.trim() === "") return true; // Optional field
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

        const validateForm = () => {
      if (!firstName.trim()) return "First name is required";
      if (!lastName.trim()) return "Last name is required";
      if (!dob.trim()) return "Date of birth is required";
    
      // Validate DOB with comprehensive calendar and business logic validation
      const dobValidation = validateDateOfBirth(dob, 50); // Minimum age 50 for seniors
      if (!dobValidation.isValid) {
        return dobValidation.error;
      }
    
      if (!gender) return "Gender is required";
      if (!roomNumber.trim()) return "Room number is required";
      if (!mobilityClass) return "Mobility class is required";
      if (!photoFile) return "Photo is required for resident identification";
    
      // Validate admission date
      if (admissionDate.trim()) {
        const admissionValidation = validateAdmissionDate(admissionDate, dob, 50); // Minimum age 50 at admission
        if (!admissionValidation.isValid) {
          return admissionValidation.error;
        }
      }
    
    // Check for duplicate room number
    const trimmedRoomNumber = roomNumber.trim();
    const duplicateRoom = existingResidents.find(resident => {
      // Extract room number from room_id (e.g., "ROOM-201" -> "201" or just "201")
      const existingRoom = (resident.room_id || resident.room_number || '')
        .replace(/^ROOM-/, '')
        .trim();
      return existingRoom.toLowerCase() === trimmedRoomNumber.toLowerCase();
    });
    
    if (duplicateRoom) {
      const residentName = `${duplicateRoom.first_name || ''} ${duplicateRoom.last_name || ''}`.trim() || 'another resident';
      return `Room number ${trimmedRoomNumber} is already assigned to ${residentName}. Please use a different room number.`;
    }
    
    // Check for duplicate MRN (only if MRN is provided)
    if (mrn.trim()) {
      const trimmedMrn = mrn.trim();
      const duplicateMrn = existingResidents.find(resident => {
        const existingMrn = (resident.mrn || '').trim();
        return existingMrn && existingMrn.toLowerCase() === trimmedMrn.toLowerCase();
      });
      
      if (duplicateMrn) {
        const residentName = `${duplicateMrn.first_name || ''} ${duplicateMrn.last_name || ''}`.trim() || 'another resident';
        return `MRN ${trimmedMrn} is already assigned to ${residentName}. Please use a different MRN or leave it blank for auto-generation.`;
      }
    }
    
        // Validate emergency contacts
    for (let i = 0; i < emergencyContacts.length; i++) {
      const contact = emergencyContacts[i];
      if (contact.contact_name.trim()) {
        // Validate phone number if provided
        if (contact.phone.trim() && !validatePhoneNumber(contact.phone)) {
          return `Invalid phone number for ${contact.contact_name}. Please use format: (555) 123-4567 or 5551234567`;
        }
        
        // Validate email if provided
        if (contact.email.trim() && !validateEmail(contact.email)) {
          return `Invalid email address for ${contact.contact_name}. Please use format: name@example.com`;
        }
      }
    }
    
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Only send what the user actually filled in — backend uses N/A for the rest
            const residentData = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        dob: dob.trim(),
        age: parseInt(age),
        gender: gender || "",
        height_cm: heightCm ? parseInt(heightCm) : "",
        weight_kg: weightKg ? parseFloat(weightKg) : "",
                room_number: roomNumber.trim(),
        bed_id: "",  // Keep empty for backend compatibility
        unit_id: unitId || "",
        mobility_class: mobilityClass || "",
        admission_date: admissionDate || "",
        mrn: mrn.trim() || "",
        fall_risk_level: fallRiskLevel || "",
        risk_factors: riskFactors,
        sleep_monitoring_consent: sleepMonitoringConsent,
        video_clip_consent: videoClipConsent,
        latest_sleep_quality: sleepQuality || "",
        // Only include contacts that have at least a name
        emergency_contacts: emergencyContacts
          .filter((c) => c.contact_name.trim())
          .map((c) => ({
            contact_name: c.contact_name.trim(),
            relationship: c.relationship || "N/A",
            phone: c.phone.trim() || "N/A",
            email: c.email.trim() || "N/A",
            contact_priority: c.contact_priority || "N/A",
            notify_on_fall: c.notify_on_fall,
            is_legal_guardian: c.is_legal_guardian,
          })),
      };

            const result = await apiService.createResident(residentData, photoFile);
      
            setCreatedResident(result.resident);
            setShowPreview(true);
    } catch (err) {
      console.error("Failed to create resident:", err);
      setError(err.message || "Failed to create resident. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

                const handleConfirm = () => {
      setShowPreview(false);
      setShowSuccessCard(true);
    };

    const handleSuccessOk = () => {
      if (onSeniorAdded && createdResident) {
        onSeniorAdded(createdResident);
      }
      // Navigate back to list
      if (onBack) {
        onBack();
      }
    };

  

  const handleCancelAndEdit = async () => {
    // Delete the created resident
    if (createdResident && createdResident.resident_id) {
      try {
        await apiService.deleteResident(createdResident.resident_id);
      } catch (err) {
        console.error('Failed to delete resident:', err);
      }
    }
        // Reset to form view with data preserved
    setShowPreview(false);
    setCreatedResident(null);
  };

  const gridCols = isMobile ? "1fr" : "1fr 1fr";

  return (
    <main
      style={{
        flex: 1,
        padding: isMobile ? "10px 10px" : "12px 16px",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minWidth: 0,
        fontFamily: "'Segoe UI', sans-serif",
        color: W,
        position: "relative",
        zIndex: 1,
        paddingTop: isMobile ? 75 : 82,
        paddingBottom: isMobile ? 10 : 30,
        marginTop: 0,
        minHeight: "100vh",
        boxSizing: "border-box",
      }}
    >
      {/* ═══ HEADER ═══ */}
      <div
        style={{
          background: CARD_BG,
          border: "1px solid #FFFFFF",
          borderRadius: 20,
          padding: isMobile ? "16px" : "20px 28px",
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}
      >
        {/* Back Button */}
        <button
          onClick={onBack}
          style={{
            background: "rgba(255,255,255,0.1)",
            border: "1.5px solid rgba(255,255,255,0.3)",
            borderRadius: 12,
            padding: "10px 14px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: W,
            fontSize: 14,
            fontWeight: 600,
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.18)";
            e.currentTarget.style.transform = "translateX(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.1)";
            e.currentTarget.style.transform = "translateX(0)";
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke={W}
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Back to Seniors
        </button>

        <h1
          style={{
            margin: 0,
            fontSize: isMobile ? 20 : 26,
            fontWeight: 700,
            flex: 1,
          }}
        >
          Add New Senior
        </h1>

                {/* Facility Badge */}
        <div
          style={{
            background: "rgba(74,144,226,0.2)",
            border: "1px solid rgba(74,144,226,0.4)",
            borderRadius: 8,
            padding: "6px 14px",
            fontSize: 12,
            fontWeight: 600,
            color: ACCENT,
            whiteSpace: "nowrap",
          }}
        >
          {facilityName}
        </div>
      </div>

                                    {/* ═══ SUCCESS CARD ═══ */}
      {showSuccessCard && createdResident && (
        <div
          style={{
            background: CARD_BG,
            border: "1px solid #FFFFFF",
            borderRadius: 20,
            padding: isMobile ? "30px 20px" : "40px 30px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 24,
            textAlign: "center",
            animation: "fadeIn 0.3s ease-out",
          }}
        >
          {/* Success Icon */}
          <div
            style={{
              width: isMobile ? 80 : 100,
              height: isMobile ? 80 : 100,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #6ADD00, #4CAF50)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 24px rgba(106,221,0,0.4)",
              animation: "scaleIn 0.4s ease-out",
            }}
          >
            <svg
              width={isMobile ? "48" : "56"}
              height={isMobile ? "48" : "56"}
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>

          {/* Success Message */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <h2
              style={{
                margin: 0,
                fontSize: isMobile ? 24 : 32,
                fontWeight: 700,
                color: GREEN,
              }}
            >
              Senior Added Successfully!
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: isMobile ? 14 : 16,
                color: W,
                opacity: 0.8,
                lineHeight: 1.6,
              }}
            >
              <strong>{firstName} {lastName}</strong> has been successfully added to the system.
              <br />
              The senior profile is now active and ready for monitoring.
            </p>
          </div>

                    {/* Senior Info Summary */}
                    <div
                      style={{
                        background: "rgba(106,221,0,0.1)",
                        border: "1.5px solid rgba(106,221,0,0.3)",
                        borderRadius: 12,
                        padding: "16px 20px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        width: "100%",
                        maxWidth: 400,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                        <span style={{ color: W, opacity: 0.7 }}>Room Number:</span>
                        <span style={{ color: W, fontWeight: 600 }}>{roomNumber}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                        <span style={{ color: W, opacity: 0.7 }}>Age:</span>
                        <span style={{ color: W, fontWeight: 600 }}>{age} years</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                        <span style={{ color: W, opacity: 0.7 }}>Mobility:</span>
                        <span style={{ color: W, fontWeight: 600 }}>{mobilityClass}</span>
                      </div>
                      {mrn && (
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
                          <span style={{ color: W, opacity: 0.7 }}>MRN:</span>
                          <span style={{ color: W, fontWeight: 600 }}>{mrn || createdResident.mrn}</span>
                        </div>
                      )}
                    </div>

          {/* OK Button */}
          <button
            type="button"
            onClick={handleSuccessOk}
            style={{
              background: "linear-gradient(135deg, #6ADD00, #4CAF50)",
              border: "none",
              borderRadius: 12,
              padding: isMobile ? "14px 50px" : "16px 60px",
              fontSize: isMobile ? 16 : 18,
              fontWeight: 700,
              color: W,
              cursor: "pointer",
              transition: "all 0.2s",
              boxShadow: "0 4px 16px rgba(106,221,0,0.3)",
              marginTop: 8,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(106,221,0,0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 16px rgba(106,221,0,0.3)";
            }}
          >
            OK
          </button>
        </div>
      )}

                        {/* ═══ PREVIEW/CONFIRMATION PAGE ═══ */}
            {showPreview && createdResident && !showSuccessCard && (
        <div
          style={{
            background: CARD_BG,
            border: "1px solid #FFFFFF",
            borderRadius: 20,
            padding: isMobile ? "20px" : "30px",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          {/* Preview Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              paddingBottom: 20,
              borderBottom: `2px solid ${GREEN}`,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: GREEN,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ffffff"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: isMobile ? 20 : 24,
                  fontWeight: 700,
                  color: W,
                }}
              >
                Review Senior Details
              </h2>
              <p
                style={{
                  margin: "4px 0 0",
                  fontSize: 14,
                  color: W,
                  opacity: 0.7,
                }}
              >
                Please review the information below and confirm to save
              </p>
            </div>
          </div>

          {/* Photo and Basic Info */}
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 24,
              alignItems: isMobile ? "center" : "flex-start",
            }}
          >
            {/* Photo */}
            {photoPreview && (
              <div
                style={{
                  width: isMobile ? 120 : 150,
                  height: isMobile ? 120 : 150,
                  borderRadius: "50%",
                  border: `3px solid ${GREEN}`,
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                <img
                  src={photoPreview}
                  alt="Senior"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </div>
            )}

            {/* Name and Info */}
            <div style={{ flex: 1 }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: isMobile ? 22 : 28,
                  fontWeight: 700,
                  color: W,
                }}
              >
                {firstName} {lastName}
              </h3>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  marginTop: 12,
                }}
              >
                <div
                  style={{
                    background: "rgba(74,144,226,0.2)",
                    border: "1px solid rgba(74,144,226,0.4)",
                    borderRadius: 8,
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: ACCENT,
                  }}
                >
                  Age: {age}
                </div>
                <div
                  style={{
                    background: "rgba(106,221,0,0.2)",
                    border: "1px solid rgba(106,221,0,0.4)",
                    borderRadius: 8,
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: GREEN,
                  }}
                >
                  Room: {roomNumber}
                </div>
                {mobilityClass && (
                  <div
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      border: "1px solid rgba(255,255,255,0.3)",
                      borderRadius: 8,
                      padding: "6px 14px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: W,
                    }}
                  >
                    {mobilityClass}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: gridCols,
              gap: 20,
              padding: "20px 0",
              borderTop: `1.5px solid ${BORDER}`,
            }}
          >
            <DetailItem label="Date of Birth" value={dob} />
            <DetailItem label="Gender" value={gender === "M" ? "Male" : "Female"} />
            <DetailItem label="Height" value={heightCm ? `${heightCm} cm` : "N/A"} />
            <DetailItem label="Weight" value={weightKg ? `${weightKg} kg` : "N/A"} />
            <DetailItem label="MRN" value={mrn || createdResident.mrn || "Auto-generated"} />
            <DetailItem label="Unit" value={unitId || "N/A"} />
            <DetailItem label="Admission Date" value={admissionDate || "N/A"} />
            <DetailItem
              label="Fall Risk Level"
              value={fallRiskLevel || "N/A"}
              valueColor={
                fallRiskLevel === "HIGH" || fallRiskLevel === "CRITICAL"
                  ? "#FF6B6B"
                  : fallRiskLevel === "MODERATE"
                  ? "#FFA500"
                  : GREEN
              }
            />
            <DetailItem label="Sleep Quality" value={sleepQuality || "N/A"} />
            <DetailItem
              label="Sleep Monitoring"
              value={sleepMonitoringConsent ? "Consented" : "Not Consented"}
              valueColor={sleepMonitoringConsent ? GREEN : "rgba(255,255,255,0.5)"}
            />
            <DetailItem
              label="Video Recording"
              value={videoClipConsent ? "Consented" : "Not Consented"}
              valueColor={videoClipConsent ? GREEN : "rgba(255,255,255,0.5)"}
            />
          </div>

          {/* Risk Factors */}
          {riskFactors.length > 0 && (
            <div
              style={{
                padding: "20px 0",
                borderTop: `1.5px solid ${BORDER}`,
              }}
            >
              <h4
                style={{
                  margin: "0 0 12px",
                  fontSize: 16,
                  fontWeight: 700,
                  color: ACCENT,
                }}
              >
                Risk Factors ({riskFactors.length})
              </h4>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {riskFactors.map((factor, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: "rgba(255,107,107,0.15)",
                      border: "1px solid rgba(255,107,107,0.4)",
                      borderRadius: 8,
                      padding: "6px 12px",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#FF6B6B",
                    }}
                  >
                    {factor.replace(/_/g, " ").toUpperCase()}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Emergency Contacts */}
          {emergencyContacts.filter(c => c.contact_name.trim()).length > 0 && (
            <div
              style={{
                padding: "20px 0",
                borderTop: `1.5px solid ${BORDER}`,
              }}
            >
              <h4
                style={{
                  margin: "0 0 12px",
                  fontSize: 16,
                  fontWeight: 700,
                  color: ACCENT,
                }}
              >
                Emergency Contacts ({emergencyContacts.filter(c => c.contact_name.trim()).length})
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {emergencyContacts
                  .filter(c => c.contact_name.trim())
                  .map((contact, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: "rgba(255,255,255,0.05)",
                        border: `1px solid ${BORDER}`,
                        borderRadius: 8,
                        padding: "10px 14px",
                        fontSize: 13,
                        color: W,
                      }}
                    >
                      <strong>{contact.contact_name}</strong>
                      {contact.relationship && ` • ${contact.relationship}`}
                      {contact.phone && ` • ${contact.phone}`}
                      {contact.contact_priority && ` • ${contact.contact_priority}`}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 16,
              paddingTop: 20,
              borderTop: `1.5px solid ${BORDER}`,
            }}
          >
            <button
              type="button"
              onClick={handleCancelAndEdit}
              style={{
                background: "rgba(255,107,107,0.15)",
                border: "1.5px solid rgba(255,107,107,0.5)",
                borderRadius: 12,
                padding: "14px 32px",
                fontSize: 15,
                fontWeight: 600,
                color: "#FF6B6B",
                cursor: "pointer",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,107,107,0.25)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,107,107,0.15)";
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FF6B6B"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
                            Cancel & Make Changes
            </button>

            <button
              type="button"
              onClick={handleConfirm}
              style={{
                background: "linear-gradient(135deg, #6ADD00, #4CAF50)",
                border: "none",
                borderRadius: 12,
                padding: "14px 40px",
                fontSize: 15,
                fontWeight: 700,
                color: W,
                cursor: "pointer",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 10,
                boxShadow: "0 4px 16px rgba(106,221,0,0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={W}
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Confirm & Save
            </button>
          </div>
        </div>
      )}

                                                {/* ═══ FORM BODY ═══ */}
      {!showPreview && !showSuccessCard && (
        <div
          style={{
            background: CARD_BG,
            border: "1px solid #FFFFFF",
            borderRadius: 20,
            padding: isMobile ? "20px" : "30px",
            display: "flex",
            flexDirection: "column",
            gap: 28,
          }}
        >
          {/* ── Photo Upload Section ── */}
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "center" : "flex-start",
              gap: isMobile ? 20 : 40,
              paddingBottom: 24,
              borderBottom: `1.5px solid ${BORDER}`,
            }}
          >
            <PhotoUpload
              photoPreview={photoPreview}
              onPhotoSelect={handlePhotoSelect}
              isMobile={isMobile}
            />
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 700,
                  color: W,
                }}
              >
                Resident Photo{" "}
                <span style={{ color: "#FF6B6B", fontSize: 14 }}>
                  * Required
                </span>
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: W,
                  opacity: 0.6,
                  lineHeight: 1.5,
                }}
              >
                Upload a clear, recent photo of the resident for identification
                purposes. This photo will be used across the dashboard for quick
                visual identification.
              </p>
              {photoFile && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={GREEN}
                    strokeWidth="2"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                  <span style={{ fontSize: 13, color: GREEN, fontWeight: 600 }}>
                    {photoFile.name}
                  </span>
                  <span style={{ fontSize: 11, opacity: 0.5 }}>
                    ({(photoFile.size / 1024).toFixed(1)} KB)
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoFile(null);
                      setPhotoPreview(null);
                    }}
                    style={{
                      background: "rgba(255,107,107,0.2)",
                      border: "1px solid rgba(255,107,107,0.4)",
                      borderRadius: 6,
                      padding: "4px 10px",
                      fontSize: 11,
                      color: "#FF6B6B",
                      cursor: "pointer",
                      marginLeft: 8,
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── Personal Information ── */}
          <div>
            <h3
              style={{
                margin: "0 0 16px",
                fontSize: 16,
                fontWeight: 700,
                color: ACCENT,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={ACCENT}
                strokeWidth="2"
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
              </svg>
              Personal Information
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: gridCols,
                gap: 16,
              }}
            >
              <FormField label="First Name" required isMobile={isMobile}>
                <TextInput
                  value={firstName}
                  onChange={setFirstName}
                  placeholder="e.g. James"
                />
              </FormField>
                            <FormField label="Last Name" required isMobile={isMobile}>
                <TextInput
                  value={lastName}
                  onChange={setLastName}
                  placeholder="e.g. Smith"
                />
              </FormField>
              <FormField label="Date of Birth" required isMobile={isMobile}>
                <TextInput
                  value={dob}
                  onChange={setDob}
                  type="date"
                />
              </FormField>
                            <FormField label="Age" required isMobile={isMobile}>
                <TextInput
                  value={age}
                  onChange={setAge}
                  placeholder="e.g. 78"
                  type="number"
                  min="50"
                  max="120"
                />
              </FormField>
                            <FormField label="Gender" required isMobile={isMobile}>
                <SelectInput
                  value={gender}
                  onChange={setGender}
                  options={GENDER_OPTIONS}
                  placeholder="Select gender"
                />
              </FormField>
              <FormField label="Height (cm)" isMobile={isMobile}>
                <TextInput
                  value={heightCm}
                  onChange={setHeightCm}
                  placeholder="e.g. 172"
                  type="number"
                  min="100"
                  max="220"
                />
              </FormField>
              <FormField label="Weight (kg)" isMobile={isMobile}>
                <TextInput
                  value={weightKg}
                  onChange={setWeightKg}
                  placeholder="e.g. 72.5"
                  type="number"
                  step="0.1"
                  min="30"
                  max="200"
                />
              </FormField>
                            <FormField label="MRN (Medical Record Number)" isMobile={isMobile}>
                <TextInput
                  value={mrn}
                  onChange={setMrn}
                  placeholder="Enter MRN"
                />
              </FormField>
            </div>
          </div>

          {/* ── Room & Unit ── */}
          <div>
            <h3
              style={{
                margin: "0 0 16px",
                fontSize: 16,
                fontWeight: 700,
                color: ACCENT,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={ACCENT}
                strokeWidth="2"
              >
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                <polyline points="9,22 9,12 15,12 15,22" />
              </svg>
              Room & Unit Assignment
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: gridCols,
                gap: 16,
              }}
            >
                            <FormField label="Room Number" required isMobile={isMobile}>
                <TextInput
                  value={roomNumber}
                  onChange={setRoomNumber}
                  placeholder="e.g. 201"
                />
              </FormField>
              <FormField label="Unit" isMobile={isMobile}>
                <SelectInput
                  value={unitId}
                  onChange={setUnitId}
                  options={[
                    "UNIT-2-north",
                    "UNIT-2-south",
                    "UNIT-3-north",
                    "UNIT-3-south",
                  ]}
                  placeholder="Select unit"
                />
              </FormField>
              <FormField label="Admission Date" isMobile={isMobile}>
                <TextInput
                  value={admissionDate}
                  onChange={setAdmissionDate}
                  type="date"
                />
              </FormField>
            </div>
          </div>

          {/* ── Clinical Information ── */}
          <div>
            <h3
              style={{
                margin: "0 0 16px",
                fontSize: 16,
                fontWeight: 700,
                color: ACCENT,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={ACCENT}
                strokeWidth="2"
              >
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
              </svg>
              Clinical Information
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: gridCols,
                gap: 16,
              }}
            >
              <FormField label="Mobility Class" required isMobile={isMobile}>
                <SelectInput
                  value={mobilityClass}
                  onChange={setMobilityClass}
                  options={MOBILITY_OPTIONS}
                  placeholder="Select mobility level"
                />
              </FormField>
              <FormField label="Fall Risk Level" isMobile={isMobile}>
                <SelectInput
                  value={fallRiskLevel}
                  onChange={setFallRiskLevel}
                  options={RISK_LEVEL_OPTIONS}
                  placeholder="Select risk level"
                />
              </FormField>
              <FormField label="Sleep Quality" isMobile={isMobile}>
                <SelectInput
                  value={sleepQuality}
                  onChange={setSleepQuality}
                  options={["GOOD", "AVERAGE", "POOR"]}
                  placeholder="Select sleep quality"
                />
              </FormField>
            </div>

            <div style={{ marginTop: 16 }}>
              <FormField label="Risk Factors" isMobile={isMobile}>
                <CheckboxGroup
                  options={RISK_FACTOR_OPTIONS}
                  selected={riskFactors}
                  onChange={setRiskFactors}
                  isMobile={isMobile}
                />
              </FormField>
            </div>
          </div>

          {/* ── Emergency Contacts ── */}
          <div>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 16,
            }}>
              <h3 style={{
                margin: 0, fontSize: 16, fontWeight: 700, color: ACCENT,
                display: "flex", alignItems: "center", gap: 8,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke={ACCENT} strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
                </svg>
                Emergency Contacts
              </h3>
              <button
                type="button"
                onClick={() => setEmergencyContacts([...emergencyContacts, { ...EMPTY_CONTACT }])}
                style={{
                  background: "rgba(74,144,226,0.2)", border: `1.5px solid ${ACCENT}`,
                  borderRadius: 8, padding: "6px 14px", fontSize: 13,
                  fontWeight: 600, color: ACCENT, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke={ACCENT} strokeWidth="2.5">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                Add Contact
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {emergencyContacts.map((contact, idx) => {
                const update = (field, val) => {
                  const copy = [...emergencyContacts];
                  copy[idx] = { ...copy[idx], [field]: val };
                  setEmergencyContacts(copy);
                };
                return (
                  <div key={idx} style={{
                    background: "rgba(255,255,255,0.05)",
                    border: `1.5px solid ${BORDER}`,
                    borderRadius: 14, padding: isMobile ? 14 : 20,
                    display: "flex", flexDirection: "column", gap: 14,
                    position: "relative",
                  }}>
                    {/* Contact # badge + remove */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: ACCENT }}>
                        Contact #{idx + 1}
                      </span>
                      {emergencyContacts.length > 1 && (
                        <button type="button" onClick={() =>
                          setEmergencyContacts(emergencyContacts.filter((_, i) => i !== idx))
                        } style={{
                          background: "rgba(255,107,107,0.15)",
                          border: "1px solid rgba(255,107,107,0.4)",
                          borderRadius: 6, padding: "4px 10px",
                          fontSize: 11, color: "#FF6B6B", cursor: "pointer",
                        }}>Remove</button>
                      )}
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 12 }}>
                      <FormField label="Full Name" isMobile={isMobile}>
                        <TextInput value={contact.contact_name}
                          onChange={(v) => update("contact_name", v)}
                          placeholder="e.g. John Doe" />
                      </FormField>
                      <FormField label="Relationship" isMobile={isMobile}>
                        <SelectInput value={contact.relationship}
                          onChange={(v) => update("relationship", v)}
                          options={RELATIONSHIP_OPTIONS}
                          placeholder="Select relationship" />
                      </FormField>
                                            <FormField label="Phone" isMobile={isMobile}>
                        <TextInput value={contact.phone}
                          onChange={(v) => update("phone", v)}
                          placeholder="e.g. (555) 123-4567"
                          type="tel" />
                      </FormField>
                      <FormField label="Email" isMobile={isMobile}>
                        <TextInput value={contact.email}
                          onChange={(v) => update("email", v)}
                          placeholder="e.g. john@email.com" type="email" />
                      </FormField>
                      <FormField label="Priority" isMobile={isMobile}>
                        <SelectInput value={contact.contact_priority}
                          onChange={(v) => update("contact_priority", v)}
                          options={PRIORITY_OPTIONS}
                          placeholder="Select priority" />
                      </FormField>
                    </div>

                    {/* Toggles row */}
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                      <label style={{
                        display: "flex", alignItems: "center", gap: 8,
                        cursor: "pointer", fontSize: 13, color: W,
                      }}>
                        <div onClick={() => update("notify_on_fall", !contact.notify_on_fall)}
                          style={{
                            width: 36, height: 20, borderRadius: 10,
                            background: contact.notify_on_fall ? GREEN : "rgba(255,255,255,0.2)",
                            position: "relative", cursor: "pointer", flexShrink: 0,
                          }}>
                          <div style={{
                            width: 14, height: 14, borderRadius: "50%",
                            background: W, position: "absolute", top: 3,
                            left: contact.notify_on_fall ? 19 : 3,
                            transition: "left 0.2s",
                          }}/>
                        </div>
                        Notify on Fall
                      </label>
                      <label style={{
                        display: "flex", alignItems: "center", gap: 8,
                        cursor: "pointer", fontSize: 13, color: W,
                      }}>
                        <div onClick={() => update("is_legal_guardian", !contact.is_legal_guardian)}
                          style={{
                            width: 36, height: 20, borderRadius: 10,
                            background: contact.is_legal_guardian ? GREEN : "rgba(255,255,255,0.2)",
                            position: "relative", cursor: "pointer", flexShrink: 0,
                          }}>
                          <div style={{
                            width: 14, height: 14, borderRadius: "50%",
                            background: W, position: "absolute", top: 3,
                            left: contact.is_legal_guardian ? 19 : 3,
                            transition: "left 0.2s",
                          }}/>
                        </div>
                        Legal Guardian
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Consent & Monitoring ── */}
          <div>
            <h3
              style={{
                margin: "0 0 16px",
                fontSize: 16,
                fontWeight: 700,
                color: ACCENT,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke={ACCENT}
                strokeWidth="2"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Consent & Monitoring
            </h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Sleep monitoring consent toggle */}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  cursor: "pointer",
                  padding: "10px 16px",
                  background: sleepMonitoringConsent
                    ? "rgba(106,221,0,0.1)"
                    : INPUT_BG,
                  border: `1.5px solid ${
                    sleepMonitoringConsent
                      ? "rgba(106,221,0,0.4)"
                      : BORDER
                  }`,
                  borderRadius: 10,
                  transition: "all 0.2s",
                }}
              >
                <div
                  onClick={() =>
                    setSleepMonitoringConsent(!sleepMonitoringConsent)
                  }
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    background: sleepMonitoringConsent ? GREEN : "rgba(255,255,255,0.2)",
                    position: "relative",
                    transition: "background 0.2s",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: W,
                      position: "absolute",
                      top: 3,
                      left: sleepMonitoringConsent ? 23 : 3,
                      transition: "left 0.2s",
                    }}
                  />
                </div>
                <span style={{ fontSize: 14, fontWeight: 500, color: W }}>
                  Sleep Monitoring Consent
                </span>
              </label>

              {/* Video clip consent toggle */}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  cursor: "pointer",
                  padding: "10px 16px",
                  background: videoClipConsent
                    ? "rgba(106,221,0,0.1)"
                    : INPUT_BG,
                  border: `1.5px solid ${
                    videoClipConsent
                      ? "rgba(106,221,0,0.4)"
                      : BORDER
                  }`,
                  borderRadius: 10,
                  transition: "all 0.2s",
                }}
              >
                <div
                  onClick={() => setVideoClipConsent(!videoClipConsent)}
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 12,
                    background: videoClipConsent ? GREEN : "rgba(255,255,255,0.2)",
                    position: "relative",
                    transition: "background 0.2s",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: W,
                      position: "absolute",
                      top: 3,
                      left: videoClipConsent ? 23 : 3,
                      transition: "left 0.2s",
                    }}
                  />
                </div>
                <span style={{ fontSize: 14, fontWeight: 500, color: W }}>
                  Video Clip Consent (Fall Recording)
                </span>
              </label>
            </div>
          </div>

                    {/* ═══ ERROR BANNER (at bottom) ═══ */}
          {error && (
            <div
              style={{
                background: "rgba(255,107,107,0.12)",
                border: "1.5px solid rgba(255,107,107,0.5)",
                borderRadius: 12,
                padding: "14px 20px",
                display: "flex",
                alignItems: "center",
                gap: 12,
                animation: "fadeIn 0.3s ease-out",
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FF6B6B"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
              <span style={{ fontSize: 14, color: "#FF6B6B", fontWeight: 500 }}>
                {error}
              </span>
              <button
                onClick={() => setError(null)}
                style={{
                  marginLeft: "auto",
                  background: "transparent",
                  border: "none",
                  color: "#FF6B6B",
                  cursor: "pointer",
                  fontSize: 18,
                }}
              >
                ✕
              </button>
            </div>
          )}

          {/* ── Submit Buttons ── */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 16,
              paddingTop: 16,
              borderTop: `1.5px solid ${BORDER}`,
            }}
          >
            <button
              type="button"
              onClick={onBack}
              disabled={submitting}
              style={{
                background: "rgba(255,255,255,0.08)",
                border: `1.5px solid ${BORDER}`,
                borderRadius: 12,
                padding: "14px 32px",
                fontSize: 15,
                fontWeight: 600,
                color: W,
                cursor: submitting ? "not-allowed" : "pointer",
                transition: "all 0.2s",
                opacity: submitting ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (!submitting)
                  e.currentTarget.style.background = "rgba(255,255,255,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.08)";
              }}
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              style={{
                background: submitting
                  ? "rgba(106,221,0,0.3)"
                  : "linear-gradient(135deg, #6ADD00, #4CAF50)",
                border: "none",
                borderRadius: 12,
                padding: "14px 40px",
                fontSize: 15,
                fontWeight: 700,
                color: submitting ? "rgba(255,255,255,0.6)" : W,
                cursor: submitting ? "not-allowed" : "pointer",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 10,
                boxShadow: submitting
                  ? "none"
                  : "0 4px 16px rgba(106,221,0,0.3)",
              }}
              onMouseEnter={(e) => {
                if (!submitting) e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              {submitting ? (
                <>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={W}
                    strokeWidth="2"
                    style={{
                      animation: "spin 1s linear infinite",
                    }}
                  >
                    <path d="M21 12a9 9 0 11-6.219-8.56" />
                  </svg>
                  Adding Senior...
                </>
              ) : (
                <>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={W}
                    strokeWidth="2.5"
                  >
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Add Senior
                </>
              )}
            </button>
          </div>
        </div>
      )}

                        {/* Animations */}
    </main>
  );
}
